// Fechas de la Agenda (D49): año, mes, semana y dia. Todo en hora local; las semanas empiezan el lunes.

export type VistaAgenda = 'anio' | 'mes' | 'semana' | 'dia';
// De lejos a cerca: el zoom de la Agenda sube o baja un nivel.
export const NIVELES_AGENDA: VistaAgenda[] = ['anio', 'mes', 'semana', 'dia'];

const medianoche = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

// Lunes de la semana de una fecha.
export function inicioSemana(d: Date) {
  const x = medianoche(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}

const sumarDias = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// "2026-10-05" en hora local (para agrupar citas por dia).
export const claveDia = (d: Date | string) => {
  const x = typeof d === 'string' ? new Date(d) : d;
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

// Los dias que se ven en el mes: de lunes a domingo, desde la semana del 1 hasta la del ultimo dia (5 o 6 semanas).
export function cuadriculaMes(fecha: Date) {
  const primero = new Date(fecha.getFullYear(), fecha.getMonth(), 1);
  const ultimo = new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0);
  const desde = inicioSemana(primero);
  const hasta = sumarDias(inicioSemana(ultimo), 6);
  const dias: Date[] = [];
  for (let d = desde; d <= hasta; d = sumarDias(d, 1)) dias.push(d);
  return dias;
}

// Rango [desde, hasta) que pide cada vista.
export function rango(vista: VistaAgenda, fecha: Date) {
  if (vista === 'dia') {
    const desde = medianoche(fecha);
    return { desde, hasta: sumarDias(desde, 1) };
  }
  if (vista === 'semana') {
    const desde = inicioSemana(fecha);
    return { desde, hasta: sumarDias(desde, 7) };
  }
  if (vista === 'mes') {
    const dias = cuadriculaMes(fecha);
    return { desde: dias[0], hasta: sumarDias(dias[dias.length - 1], 1) };
  }
  return { desde: new Date(fecha.getFullYear(), 0, 1), hasta: new Date(fecha.getFullYear() + 1, 0, 1) };
}

// Lo que se pide al servidor: el año entero de la fecha (y la semana si cruza de año). Asi el zoom entre niveles ya
// tiene las citas y la animacion no espera.
export function rangoCarga(vista: VistaAgenda, fecha: Date) {
  const r = rango(vista, fecha);
  const a = rango('anio', fecha);
  return { desde: r.desde < a.desde ? r.desde : a.desde, hasta: r.hasta > a.hasta ? r.hasta : a.hasta };
}

// Un dia, una semana, un mes o un año antes o despues.
export function mover(vista: VistaAgenda, fecha: Date, n: number) {
  if (vista === 'dia') return sumarDias(medianoche(fecha), n);
  if (vista === 'semana') return sumarDias(inicioSemana(fecha), 7 * n);
  if (vista === 'mes') return new Date(fecha.getFullYear(), fecha.getMonth() + n, 1);
  return new Date(fecha.getFullYear() + n, 0, 1);
}

// Titulo de la vista: "28 de septiembre – 4 de octubre de 2026", "octubre de 2026", "2026".
export function tituloVista(vista: VistaAgenda, fecha: Date) {
  if (vista === 'anio') return String(fecha.getFullYear());
  if (vista === 'mes') return fecha.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
  if (vista === 'dia') return fecha.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const lunes = inicioSemana(fecha);
  const domingo = sumarDias(lunes, 6);
  return `${lunes.toLocaleDateString('es-CO', { day: 'numeric', month: 'long' })} – ${domingo.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

// Cuantas citas hay por dia ("2026-10-05" -> n).
export function porDia<T extends { inicio: string }>(citas: T[]) {
  const m = new Map<string, T[]>();
  for (const c of citas) m.set(claveDia(c.inicio), [...(m.get(claveDia(c.inicio)) ?? []), c]);
  return m;
}

// Lee "?vista=mes&fecha=2026-10-01" (lo que no se entiende queda en la semana de hoy).
export function leerVista(params: URLSearchParams, hoy = new Date()) {
  const v = params.get('vista');
  const vista: VistaAgenda = v === 'mes' || v === 'anio' || v === 'dia' ? v : 'semana';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(params.get('fecha') ?? '');
  const fecha = m ? new Date(+m[1], +m[2] - 1, +m[3]) : medianoche(hoy);
  return { vista, fecha: Number.isNaN(fecha.getTime()) ? medianoche(hoy) : fecha };
}

// De quien es una cita, para la tarjeta: nada si es mia, el nombre si es de otro, "Sin tomar" si nadie la tomo.
export function deQuien(c: { responsableId: string | null }, yo: string | undefined, nombre?: string) {
  if (c.responsableId && c.responsableId === yo) return '';
  return c.responsableId ? nombre || 'Otra persona' : 'Sin tomar';
}

// Horas que muestra la rejilla de Semana y Dia: de 7 a 19, o mas si alguna cita empieza antes o termina despues.
export function horasVisibles(citas: { inicio: string; fin: string | null }[]) {
  let h0 = 7;
  let h1 = 19;
  for (const c of citas) {
    const i = new Date(c.inicio);
    const f = c.fin ? new Date(c.fin) : new Date(i.getTime() + 30 * 60_000);
    h0 = Math.min(h0, i.getHours());
    h1 = Math.max(h1, f.getDate() !== i.getDate() ? 24 : Math.ceil(f.getHours() + f.getMinutes() / 60));
  }
  return { h0, h1 };
}

// Citas de un dia puestas en carriles cuando se cruzan: cada una sabe su carril y cuantos hay en su grupo.
export function enCarriles<T extends { inicio: string; fin: string | null }>(citas: T[]) {
  const desde = (c: T) => new Date(c.inicio).getTime();
  const hasta = (c: T) => (c.fin ? new Date(c.fin).getTime() : desde(c) + 30 * 60_000);
  const orden = [...citas].sort((a, b) => desde(a) - desde(b));
  const out: { c: T; carril: number; carriles: number }[] = [];
  let grupo: { c: T; carril: number; carriles: number }[] = [];
  let finGrupo = -Infinity;
  const cerrar = () => {
    const n = Math.max(1, ...grupo.map((x) => x.carril + 1));
    for (const x of grupo) x.carriles = n;
    out.push(...grupo);
    grupo = [];
  };
  for (const c of orden) {
    if (desde(c) >= finGrupo && grupo.length) cerrar();
    const ocupados = grupo.filter((x) => hasta(x.c) > desde(c)).map((x) => x.carril);
    let carril = 0;
    while (ocupados.includes(carril)) carril++;
    grupo.push({ c, carril, carriles: 1 });
    finGrupo = Math.max(finGrupo, hasta(c));
  }
  if (grupo.length) cerrar();
  return out;
}

// Una reunion de la central 3CX llega dos veces: como "Reunion de 3CX" (sincronizada) y como la invitacion que 3CX
// mando por correo. Se deja una sola (la de 3CX), comparando el codigo del enlace /meet/<codigo> (D97).
const codigoMeet = (enlace: string) => /\/meet\/([A-Za-z0-9_-]+)/.exec(enlace)?.[1] ?? null;
export function sinDuplicados<T extends { origen: string; enlace: string }>(citas: T[]) {
  const de3cx = new Set(citas.filter((c) => c.origen === 'TRESCX').map((c) => codigoMeet(c.enlace)).filter(Boolean));
  return citas.filter((c) => c.origen === 'TRESCX' || !de3cx.has(codigoMeet(c.enlace)));
}
